import * as d3 from "d3";
import React, { useLayoutEffect } from "react";

import {
  DateTimelineProps,
  LineSeriesType,
} from "@/components/organisms/DateTimeline/DateTimeline.types";

interface DateTimelineLineSeriesPropsType<T> {
  data: T[];
  dateKey: keyof T;
  g: d3.Selection<SVGGElement, unknown, null, undefined>;
  isInitialized: React.MutableRefObject<boolean>;
  series: LineSeriesType;
  seriesIndex: number;
  style: any;
  tooltip?: DateTimelineProps<T>["tooltip"];
  xScale: d3.ScaleBand<Date>;
  yScale: d3.ScaleLinear<number, number>;
  selectedRange?: [Date, Date] | null;
  chartHeight: number;
}

export function DateTimelineLineSeries<T>({
  data,
  dateKey,
  g,
  isInitialized,
  series,
  seriesIndex,
  style,
  tooltip,
  xScale,
  yScale,
  selectedRange,
  chartHeight,
}: DateTimelineLineSeriesPropsType<T>) {
  useLayoutEffect(() => {
    if (!g) return;

    const line = d3
      .line<T>()
      .x((d) => xScale(d[dateKey] as Date)! + xScale.bandwidth() / 2)
      .y((d) => yScale(d[series.valueKey] || 0))
      .curve(
        series.curveType === "monotone" ? d3.curveMonotoneX : d3.curveLinear,
      );

    // Unselected Line
    const pathSelection = g.selectAll(`.line-series-${seriesIndex}`);
    const pathUpdate = pathSelection.data([data]);
    const pathEnter = pathUpdate
      .enter()
      .append("path")
      .attr("class", `line line-series-${seriesIndex}`)
      .attr("fill", "none")
      .attr("stroke", style.color)
      .attr("stroke-width", style.strokeWidth);
    const mergedPath = pathUpdate.merge(pathEnter);

    // // Selected Line
    const clipId = `line-series-clip-${seriesIndex}`;

    g.select(`#${clipId}`).remove(); // Remove existing clip path

    if (selectedRange) {
      const startX = xScale(selectedRange[0]);
      const endX = xScale(selectedRange[1]);
      if (startX !== undefined && endX !== undefined) {
        g.append("clipPath")
          .attr("id", clipId)
          .append("rect")
          .attr("x", startX + xScale.bandwidth() / 2)
          .attr("y", 0)
          .attr("width", endX - startX)
          .attr("height", chartHeight);
      }
    }

    const selectedPathSelection = g.selectAll(
      `.line-series-selected-${seriesIndex}`,
    );
    const selectedPathUpdate = selectedPathSelection.data([data]);
    const selectedPathEnter = selectedPathUpdate
      .enter()
      .append("path")
      .attr("class", `line line-series-selected-${seriesIndex}`)
      .attr("fill", "none")
      .attr("stroke", style.selectedColor)
      .attr("stroke-width", style.strokeWidth)
      .attr("clip-path", selectedRange ? `url(#${clipId})` : null);
    const mergedSelectedPath = selectedPathUpdate.merge(selectedPathEnter);
    mergedSelectedPath.attr("d", line);

    if (!isInitialized.current) {
      const lineInitial = d3
        .line<T>()
        .x((d) => xScale(d[dateKey] as Date)! + xScale.bandwidth() / 2)
        .y(() => yScale(0));

      mergedPath
        .attr("d", lineInitial)
        .transition()
        .duration(750)
        .attr("d", line);
      mergedSelectedPath
        .attr("d", lineInitial)
        .transition()
        .duration(750)
        .attr("d", line);
    } else {
      mergedPath.attr("d", line);
    }

    if (tooltip) {
      const dotSelection = g
        .selectAll<SVGCircleElement, T>(`.line-dots-${seriesIndex}`)
        .data<T>(data as T[]);
      const dotEnter = dotSelection
        .enter()
        .append("circle")
        .attr("class", `line-dot line-dots-${seriesIndex}`)
        .attr("r", 10)
        .attr("fill", "transparent");
      const dotMerged = dotEnter.merge(dotSelection as any);
      dotMerged
        .attr(
          "cx",
          (d: T) => xScale(d[dateKey] as Date)! + xScale.bandwidth() / 2,
        )
        .attr("cy", (d: any) => yScale((d as any)[series.valueKey] || 0));
    }
  }, [
    g,
    series,
    xScale,
    yScale,
    data,
    dateKey,
    seriesIndex,
    isInitialized,
    style,
    tooltip,
    selectedRange,
    chartHeight,
  ]);

  return null;
}
